import store from '../../../store';
import setGlobalState from '../../../redux/setGlobalState';
import type {AiAgentPendingProposal, AiAgentProposalOwner} from '../../../redux/aiAgent/aiAgent.type';

/**
 * The prompt the confirm button submits. It never reaches the server: the turn runs the
 * proposal the previous turn only summarized, so the summary and the creation stay two
 * separate turns with the producer's answer between them.
 */
export const CONFIRM_PROPOSAL_PROMPT = '__aiAgentConfirmProposal__';

export function isProposalConfirmPrompt(prompt: string): boolean {
    return prompt === CONFIRM_PROPOSAL_PROMPT;
}

export function holdProposal(proposal: AiAgentPendingProposal, owner: AiAgentProposalOwner): void {
    setGlobalState('aiAgent.pendingProposal', proposal);
    setGlobalState('aiAgent.pendingProposalOwner', owner);
}

export function clearHeldProposal(): void {
    setGlobalState('aiAgent.pendingProposal', null);
    setGlobalState('aiAgent.pendingProposalOwner', null);
}

const isSameOwner = (a: AiAgentProposalOwner, b: AiAgentProposalOwner): boolean =>
    a.scope === b.scope && a.eid === b.eid && a.pid === b.pid;

export type TakeHeldProposalResult =
    | {status: 'taken'; proposal: AiAgentPendingProposal}
    | {status: 'none'}
    | {status: 'otherOwner'};

/**
 * Hands the proposal over only to the event/package it was summarized for. The panel lives in
 * RootLayout and the chat survives navigation, so a summary agreed on one event can still be
 * on screen after moving to another — running it there would create the record on the wrong
 * one. A mismatch keeps the proposal held: going back and confirming there still works.
 *
 * Taking it also drops it — one confirmation creates at most one draft.
 */
export function takeHeldProposal(owner: AiAgentProposalOwner): TakeHeldProposalResult {
    const {pendingProposal, pendingProposalOwner} = store.getState().aiAgent ?? {};
    if(!pendingProposal) {
        return {status: 'none'};
    }
    if(!pendingProposalOwner || !isSameOwner(pendingProposalOwner, owner)) {
        return {status: 'otherOwner'};
    }
    clearHeldProposal();
    return {status: 'taken', proposal: pendingProposal};
}
